/**
 * Airport Timezone Service
 *
 * Returns the IANA timezone for an airport code.
 *
 * For common airports, uses an in-memory lookup table (no API call).
 * For unknown codes, falls back to UTC.
 *
 * Future: could fall back to a server-side lookup service if needed.
 */

// Common US + international airports → IANA timezone
// Covers the vast majority of real traveler usage.
const AIRPORT_TIMEZONES = {
  // ── US: Eastern ─────────────────────────────────────────────────────
  ATL: 'America/New_York', BOS: 'America/New_York', BWI: 'America/New_York',
  BUF: 'America/New_York', CLE: 'America/New_York', CLT: 'America/New_York',
  CMH: 'America/New_York', DCA: 'America/New_York', DTW: 'America/New_York',
  EWR: 'America/New_York', IAD: 'America/New_York', JAX: 'America/New_York',
  JFK: 'America/New_York', LGA: 'America/New_York', MIA: 'America/New_York',
  MCO: 'America/New_York', PBI: 'America/New_York', PHL: 'America/New_York',
  PIT: 'America/New_York', RDU: 'America/New_York', RIC: 'America/New_York',
  RSW: 'America/New_York', SRQ: 'America/New_York', SYR: 'America/New_York',
  TPA: 'America/New_York', FLL: 'America/New_York', ROC: 'America/New_York',
  ALB: 'America/New_York', BDL: 'America/New_York', MHT: 'America/New_York',
  PWM: 'America/New_York', PVD: 'America/New_York',
  IND: 'America/Indiana/Indianapolis',

  // ── US: Central ─────────────────────────────────────────────────────
  AUS: 'America/Chicago', BHM: 'America/Chicago', BNA: 'America/Chicago',
  DAL: 'America/Chicago', DFW: 'America/Chicago', DSM: 'America/Chicago',
  HOU: 'America/Chicago', IAH: 'America/Chicago', MCI: 'America/Chicago',
  MDW: 'America/Chicago', MEM: 'America/Chicago', MKE: 'America/Chicago',
  MSP: 'America/Chicago', MSY: 'America/Chicago', OKC: 'America/Chicago',
  OMA: 'America/Chicago', ORD: 'America/Chicago', SAT: 'America/Chicago',
  STL: 'America/Chicago', TUL: 'America/Chicago', XNA: 'America/Chicago',
  LIT: 'America/Chicago', SHV: 'America/Chicago', JAN: 'America/Chicago',

  // ── US: Mountain ────────────────────────────────────────────────────
  ABQ: 'America/Denver', BOI: 'America/Boise', BIL: 'America/Denver',
  COS: 'America/Denver', DEN: 'America/Denver', ELP: 'America/Denver',
  GEG: 'America/Los_Angeles', SLC: 'America/Denver', TUS: 'America/Phoenix',
  PHX: 'America/Phoenix', MSO: 'America/Denver',

  // ── US: Pacific ─────────────────────────────────────────────────────
  BUR: 'America/Los_Angeles', LAS: 'America/Los_Angeles', LAX: 'America/Los_Angeles',
  OAK: 'America/Los_Angeles', ONT: 'America/Los_Angeles', PDX: 'America/Los_Angeles',
  RNO: 'America/Los_Angeles', SAN: 'America/Los_Angeles', SEA: 'America/Los_Angeles',
  SFO: 'America/Los_Angeles', SJC: 'America/Los_Angeles', SMF: 'America/Los_Angeles',
  SNA: 'America/Los_Angeles',

  // ── US: Alaska / Hawaii ─────────────────────────────────────────────
  ANC: 'America/Anchorage', FAI: 'America/Anchorage', JNU: 'America/Juneau',
  HNL: 'Pacific/Honolulu', OGG: 'Pacific/Honolulu', KOA: 'Pacific/Honolulu',
  LIH: 'Pacific/Honolulu',

  // ── Canada ──────────────────────────────────────────────────────────
  YYZ: 'America/Toronto', YUL: 'America/Toronto', YOW: 'America/Toronto',
  YHZ: 'America/Halifax', YYC: 'America/Edmonton', YEG: 'America/Edmonton',
  YVR: 'America/Vancouver', YWG: 'America/Winnipeg',

  // ── Mexico & Caribbean ──────────────────────────────────────────────
  MEX: 'America/Mexico_City', CUN: 'America/Cancun', PVR: 'America/Bahia_Banderas',
  SJD: 'America/Mazatlan', GDL: 'America/Mexico_City',
  SJU: 'America/Puerto_Rico', STI: 'America/Santo_Domingo',
  NAS: 'America/Nassau', MBJ: 'America/Jamaica',

  // ── Europe (major) ──────────────────────────────────────────────────
  LHR: 'Europe/London', LGW: 'Europe/London', MAN: 'Europe/London',
  EDI: 'Europe/London', DUB: 'Europe/Dublin',
  CDG: 'Europe/Paris', ORY: 'Europe/Paris', NCE: 'Europe/Paris',
  FRA: 'Europe/Berlin', MUC: 'Europe/Berlin', BER: 'Europe/Berlin',
  AMS: 'Europe/Amsterdam', BRU: 'Europe/Brussels',
  MAD: 'Europe/Madrid', BCN: 'Europe/Madrid',
  FCO: 'Europe/Rome', MXP: 'Europe/Rome', LIN: 'Europe/Rome',
  ZRH: 'Europe/Zurich', GVA: 'Europe/Zurich', VIE: 'Europe/Vienna',
  CPH: 'Europe/Copenhagen', ARN: 'Europe/Stockholm', OSL: 'Europe/Oslo',
  HEL: 'Europe/Helsinki', IST: 'Europe/Istanbul', ATH: 'Europe/Athens',
  LIS: 'Europe/Lisbon', OPO: 'Europe/Lisbon',

  // ── Asia / Pacific ──────────────────────────────────────────────────
  HND: 'Asia/Tokyo', NRT: 'Asia/Tokyo', KIX: 'Asia/Tokyo',
  ICN: 'Asia/Seoul', GMP: 'Asia/Seoul',
  PEK: 'Asia/Shanghai', PVG: 'Asia/Shanghai', SHA: 'Asia/Shanghai',
  HKG: 'Asia/Hong_Kong', TPE: 'Asia/Taipei',
  SIN: 'Asia/Singapore', KUL: 'Asia/Kuala_Lumpur', BKK: 'Asia/Bangkok',
  MNL: 'Asia/Manila', CGK: 'Asia/Jakarta', HAN: 'Asia/Ho_Chi_Minh',
  SGN: 'Asia/Ho_Chi_Minh', DEL: 'Asia/Kolkata', BOM: 'Asia/Kolkata',
  DXB: 'Asia/Dubai', AUH: 'Asia/Dubai', DOH: 'Asia/Qatar',
  SYD: 'Australia/Sydney', MEL: 'Australia/Melbourne', BNE: 'Australia/Brisbane',
  AKL: 'Pacific/Auckland',

  // ── South America ───────────────────────────────────────────────────
  GRU: 'America/Sao_Paulo', GIG: 'America/Sao_Paulo',
  EZE: 'America/Argentina/Buenos_Aires',
  SCL: 'America/Santiago', LIM: 'America/Lima', BOG: 'America/Bogota',
};

class AirportTimezoneService {
  /**
   * Get IANA timezone for an airport code.
   * @param {string} airportCode - IATA (e.g. 'DFW') or ICAO (e.g. 'KDFW')
   * @returns {Promise<string>} IANA timezone (e.g. 'America/Chicago')
   */
  async getAirportTimezone(airportCode) {
    if (!airportCode) return 'UTC';

    // Normalize: uppercase, strip K prefix for ICAO codes
    let code = String(airportCode).toUpperCase().trim();
    if (code.length === 4 && code.startsWith('K')) {
      code = code.substring(1);
    }

    return AIRPORT_TIMEZONES[code] || 'UTC';
  }

  /**
   * Synchronous version — used where we don't want a Promise.
   */
  getAirportTimezoneSync(airportCode) {
    if (!airportCode) return 'UTC';
    let code = String(airportCode).toUpperCase().trim();
    if (code.length === 4 && code.startsWith('K')) {
      code = code.substring(1);
    }
    return AIRPORT_TIMEZONES[code] || 'UTC';
  }
}

const airportTimezoneService = new AirportTimezoneService();
export default airportTimezoneService;
